import {
  guardFail,
  guardOk,
  IMAGE_GEN_GUARD_CODE,
  type GuardResult,
} from "./guards";

/** 与 ClientUi / 后端 ConfiguredImageModel 对齐的最小模型条目 */
export type ImageGenModelCatalogEntry = {
  platform: string;
  id: string;
  supportsReferenceImages?: boolean;
};

export function normalizeImageGenPlatformId(platform: string): string {
  const v = platform.trim().toLowerCase();
  return v === "shiyun" ? "shiyunapi" : v;
}

export function normalizeImageGenSizeToken(size: string): string {
  return size.trim().replace(/[×✕]/g, "x");
}

/** 是否存在至少一个支持参考图的模型（漫画生图入口） */
export function guardHasReferenceCapableModels(
  models: readonly ImageGenModelCatalogEntry[],
): GuardResult {
  const capable = models.filter((m) => m.supportsReferenceImages === true);
  if (capable.length > 0) return guardOk();
  return guardFail(
    "当前没有支持参考图的平台或模型，请在后台启用对应生图平台后重试。",
    IMAGE_GEN_GUARD_CODE.NO_REFERENCE_CAPABLE_MODEL,
  );
}

/** 漫画 settings：平台 + 模型须在支持参考图的目录内 */
export function guardComicReferenceModel(
  settings: { platformId: string; modelId: string },
  models: readonly ImageGenModelCatalogEntry[],
): GuardResult {
  const platformId = normalizeImageGenPlatformId(settings.platformId);
  const modelId = settings.modelId.trim();
  if (!platformId) {
    return guardFail("请在生图设置中选择平台。", IMAGE_GEN_GUARD_CODE.PLATFORM_MISSING);
  }
  if (!modelId) {
    return guardFail(
      "请在生图设置中选择支持参考图的有效模型。",
      IMAGE_GEN_GUARD_CODE.MODEL_MISSING,
    );
  }
  const ok = models.some(
    (m) =>
      m.supportsReferenceImages === true &&
      normalizeImageGenPlatformId(m.platform) === platformId &&
      m.id === modelId,
  );
  if (!ok) {
    return guardFail(
      "请在生图设置中选择支持参考图的有效模型。",
      IMAGE_GEN_GUARD_CODE.MODEL_INVALID,
    );
  }
  return guardOk();
}

/** apiSize 须在模型可选尺寸列表内（列表为空时不校验） */
export function guardApiSizeInCatalog(
  apiSize: string,
  allowedSizes: readonly string[],
): GuardResult {
  if (allowedSizes.length === 0) return guardOk();
  const normalized = normalizeImageGenSizeToken(apiSize);
  if (!normalized) {
    return guardFail("请在生图设置中选择输出尺寸。", IMAGE_GEN_GUARD_CODE.SIZE_MISSING);
  }
  const allowed = new Set(allowedSizes.map((s) => normalizeImageGenSizeToken(s).toLowerCase()));
  if (!allowed.has(normalized.toLowerCase())) {
    return guardFail(
      "当前尺寸已失效，请在生图设置中重新选择输出尺寸。",
      IMAGE_GEN_GUARD_CODE.SIZE_INVALID,
    );
  }
  return guardOk();
}

/** 独立创作页：平台 / 模型须显式选择；尺寸/比例按 dimensionMode 择一校验 */
export function guardStandaloneImageGenSelection(input: {
  platformId: string;
  modelId: string;
  apiSize: string;
  /** 模型维度模式，缺省按 size 校验（向后兼容） */
  dimensionMode?: "size" | "aspect_ratio";
}): GuardResult {
  if (!input.platformId.trim()) {
    return guardFail("请选择 AI 平台", IMAGE_GEN_GUARD_CODE.PLATFORM_MISSING);
  }
  if (!input.modelId.trim()) {
    return guardFail("请选择出图模型", IMAGE_GEN_GUARD_CODE.MODEL_MISSING);
  }
  // dimensionMode=aspect_ratio 时不要求 apiSize（因为 API 会发 aspect_ratio）
  if (input.dimensionMode !== "aspect_ratio" && !input.apiSize.trim()) {
    return guardFail("请选择出图尺寸", IMAGE_GEN_GUARD_CODE.SIZE_MISSING);
  }
  return guardOk();
}
